import {
  delay,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import { change } from 'redux-form';

import * as api from '../../services/pricebook/apiPricebook';
import * as apiCommon from '../../services/apiCommon';
import * as apiTaxCode from '../../services/tax/apiTaxCode';

import * as common from '../sagasCommon';
import * as utilPreference from '../../services/preference/utilty_preference';
import { browserHistory } from 'react-router';
import * as SagasType from '../../constants/constant_saga_types';
import { PRICEBOOK } from '../../constants/constant_doc_types';
import { PriceBookForm } from '../../constants/constant_form_names';

import * as sectionTableActions from '../../components/common/form/SectionTable/actions';

import moment from 'moment';
import { getPrimarySubsidiaryCurrency } from '../../common/selectors/selector_common';
import { getPreferenceSettings } from '../../actions/actionCreatorsAdmin';
import { getErrorMessage } from '../../utils';
import {
  getCurrencyCode,
  getPriceLevels,
} from '../../components/admin/pricebook/PriceBookSelector';
import { formatCurrency } from '../../services/currency/utility_currency';
import { calculateQuantityBreaks } from './utils_price_book';
import { getLocalDateOnly } from '../../utils/local_date';

export function* fetchPriceBookForRenewSaga(feathersApp) {
  yield takeEvery(
    'FETCH_PRICEBOOK_FOR_RENEW',
    callFetchPriceBookForRenew,
    feathersApp,
  );
}
function* callFetchPriceBookForRenew(feathersApp, action) {
  const { tenant, user } = yield select();
  const { id, silent } = action;
  try {
    if (id) {
      yield common.showProgressDataById(silent);
      const fetchParams = { tenant, user, id };
      const renewPricebook = yield call(
        api.fetchPriceBookById,
        feathersApp,
        fetchParams,
      );
      delete renewPricebook.id;
      const { price_book_reference } = renewPricebook;
      // get only the dates
      const dataParams = {
        filters: {
          price_book_reference,
          sorted_columns: {
            available_to: {
              direction: 'desc',
            },
          },
          limit: 1,
        },
        list_type: PRICEBOOK,
      };
      const { data } = yield call(apiCommon.getDocs, feathersApp, dataParams);
      const latestToDate = data[0].available_to;
      if (!latestToDate) {
        renewPricebook.available_from = null;
      } else {
        // assign latest available_to to doc.available_from
        // if past date then use today's date
        // if future date then increment that future date by 1
        const today = moment().startOf('day');
        const latestDate = moment(latestToDate).startOf('day');
        const isPastDate = latestDate.isBefore(today);
        const futureDate = latestDate.add(1, 'days');
        renewPricebook.available_from = isPastDate ? today : futureDate;
      }

      yield common.doneDataById(renewPricebook);
    }
  } catch (error) {
    yield common.showErrorDataById(error);
  }
}

export function* fetchPriceBookForEditSaga(feathersApp) {
  yield takeEvery(
    'FETCH_PRICEBOOK_FOR_EDIT',
    callFetchPriceBookForEdit,
    feathersApp,
  );
}
function* callFetchPriceBookForEdit(feathersApp, action) {
  const { tenant, user } = yield select();
  const { id, silent } = action;

  yield put(getPreferenceSettings(['allow_current_price_book_update']));
  try {
    yield common.showProgressDataById();
    if (id) {
      yield common.showProgressDataById(silent);
      const fetchParams = { tenant, user, id };
      const doc = yield call(api.fetchPriceBookById, feathersApp, fetchParams);
      const filters = { price_book_reference: doc.price_book_reference };
      const dataParams = { filters, list_type: 'price_book_items' };
      const res = yield call(apiCommon.getListData, feathersApp, dataParams);
      if (res && res.docs) {
        doc.item_locations = res.docs || [];
      }

      if (doc) {
        const taxRate = doc.tax_group_id
          ? yield call(
              apiTaxCode.getTotalTaxRate,
              feathersApp,
              doc.tax_group_id,
            )
          : 0;

        doc.tax_rate = taxRate;
        doc.retail_price = Number(doc.retail_price);
        doc.cost_base = Number(doc.cost_base);
        doc.margin_percent = Number(doc.margin_percent);
        doc.mark_up_percent = Number(doc.mark_up_percent);
        const isMargin = !!doc.margin_percent;
        doc.use_margin = isMargin;

        doc.price_levels = calculateQuantityBreaks(doc);
      }
      yield common.doneDataById(doc);
    } else {
      const keySetting = 'default_price_level';
      const preferenceParams = {
        accountId: (tenant || {}).id,
        settings: [keySetting],
      };
      const preferences = yield call(
        utilPreference.getPreferenceValues,
        feathersApp,
        preferenceParams,
      );
      const defaultCurrency = yield select(getPrimarySubsidiaryCurrency);
      const initialValues = {
        available_from: moment().startOf('day').format(),
        available_to: null,
        price_levels: [
          {
            price_level_id: preferences[keySetting],
            default: true,
            quantity_break: [{ quantity: 1, discount_percent: 0, rate: 0 }],
          },
        ],
        retail_price: 0,
        currency_id: defaultCurrency.id,
        currency: defaultCurrency,
      };
      yield common.doneDataById(initialValues);
    }
  } catch (error) {
    yield common.showErrorDataById(error);
  }
}

export function* savePriceBookSaga(feathersApp) {
  yield takeEvery('SAVE_PRICE_BOOK', callSavePriceBook, feathersApp);
}

function* callSavePriceBook(feathersApp, action) {
  const { data, silent, isRenew, previousId } = action;

  try {
    yield common.showProgressSaveRecord(silent);
    const { tenant, user } = yield select();

    if (isRenew) {
      //UPDATE PREVIOUS copied DOCUMENT
      const previousDate = moment(data.available_from).subtract(1, 'days');
      yield call(api.updatePreviousPriceBookDate, feathersApp, {
        tenant,
        user,
        data,
        previousId,
        previousDate,
      });
    }

    const result = yield call(api.savePriceBook, feathersApp, {
      tenant,
      user,
      data,
      isRenew,
      previousId,
    });

    browserHistory.push(`/admin/pricebook/edit/${result.id}`);

    const { tax_group_id } = result;
    const taxRate = tax_group_id
      ? yield call(apiTaxCode.getTotalTaxRate, feathersApp, tax_group_id)
      : 0;
    result.tax_rate = taxRate;
    result.price_levels = calculateQuantityBreaks(result);
    yield common.doneSaveRecord(result, 'Pricebook was saved');
    yield common.doneDataById(result);
  } catch (error) {
    yield common.showErrorSaveRecord(error);
  }
}

export function* fetchListPriceBookSaga(feathersApp) {
  yield takeLatest('FETCH_LIST_PRICEBOOK', callFetchListPricebook, feathersApp);
}

function* callFetchListPricebook(feathersApp, action) {
  try {
    const { silent, filters } = action;
    yield common.showProgressListTable(silent);
    yield delay(500); // for searching
    const { available_from, available_to } = filters;
    const dataParams = {
      filters: {
        ...filters,
        available_from: getLocalDateOnly(available_from),
        available_to: getLocalDateOnly(available_to),
      },
      list_type: 'price_book',
    };
    const tableData = yield call(apiCommon.getDocs, feathersApp, dataParams);
    yield call(common.doneListTable, {
      rows: tableData.data,
      total_rows: tableData.total,
    });
  } catch (error) {
    yield common.showErrorListTable(error);
  }
}

export function* deletePricebookFromListSaga(feathersApp) {
  yield takeEvery(
    'DELETE_PRICEBOOK_FROM_LIST',
    callDeletePricebookFromList,
    feathersApp,
  );
}
function* callDeletePricebookFromList(feathersApp, action) {
  yield deletePricebook(feathersApp, action);
  yield delay(2000);
  yield callFetchListPricebook(feathersApp, { silent: true });
}

export function* deletePricebookSaga(feathersApp) {
  yield takeEvery('DELETE_PRICEBOOK', callDeletePricebook, feathersApp);
}
function* callDeletePricebook(feathersApp, action) {
  const result = yield deletePricebook(feathersApp, action);
  yield delay(2000);
  if (result) {
    browserHistory.push('/admin/pricebook');
  }
}

function* deletePricebook(feathersApp, action) {
  try {
    yield put({ type: 'DELETE_RECORD_INPROGRESS' });
    const { id } = action;
    let result = null;
    if (id) {
      const { tenant, user } = yield select();
      const { id } = action;
      const params = { tenant, user, id };
      result = yield call(api.deletePricebook, feathersApp, params);
    }
    yield put({
      type: 'DELETE_RECORD_DONE',
      payload: { id, docType: PRICEBOOK },
    });
    return result;
  } catch (error) {
    yield put({ type: 'DELETE_RECORD_ERROR', error });
  }
}

export function* getTaxCodes(feathersApp) {
  yield takeEvery(
    SagasType.GET_PRICEBOOK_TAX_CODE,
    callGetTaxCodeByTaxGroup,
    feathersApp,
  );
}
function* callGetTaxCodeByTaxGroup(feathersApp, action) {
  try {
    const { tax_group_id } = action;
    const taxRate = yield call(
      apiTaxCode.getTotalTaxRate,
      feathersApp,
      tax_group_id,
    );
    yield put(change(PriceBookForm, 'tax_rate', taxRate));

    const priceLevels = yield select(getPriceLevels);
    const currencyCode = yield select(getCurrencyCode);
    for (let i = 0; i < priceLevels.length; i++) {
      const priceLevel = priceLevels[i];
      const quantityBreaks = priceLevel.quantity_break || [];

      for (let x = 0; x < quantityBreaks.length; x++) {
        const quantityBreak = quantityBreaks[x];
        const { rate, quantity } = quantityBreak;
        if (rate) {
          const rateIncTax = Number(rate) + Number(rate) * taxRate;
          yield put(
            change(
              PriceBookForm,
              `price_levels[${i}].quantity_break[${x}].rate_inc_tax`,
              formatCurrency(rateIncTax, currencyCode),
            ),
          );
          yield put(
            change(
              PriceBookForm,
              `price_levels[${i}].quantity_break[${x}].quantity_price_inc_tax`,
              formatCurrency(quantity * rateIncTax, currencyCode),
            ),
          );
        }
      }
    }
  } catch (e) {
    console.error(e);
  }
}

export function* getPriceBookItemLocation(app) {
  yield takeEvery(
    'GET_PRICE_BOOK_ITEM_LOCATION',
    callGetPriceBookItemLocation,
    app,
  );
}
function* callGetPriceBookItemLocation(app, action) {
  const { key, payload } = action;
  yield put(sectionTableActions.setSectionTableLoader(key, true));

  try {
    const { data, total, limit, skip } = yield call(apiCommon.getDocs, app, {
      ...payload,
      filters: {
        ...payload.filters,
        location_type: 'store',
      },
      list_type: 'item_location',
    });

    const meta = {
      total,
      limit,
      skip,
    };

    yield put(sectionTableActions.setSectionTableData(key, { data, meta }));
  } catch (err) {
    yield put(
      sectionTableActions.setSectionTableError(key, true, getErrorMessage(err)),
    );
  }
}
